import { PortfolioRateGroupOverview } from '@mews-commander/data-access';
import { SteppedDialogFormProps, useSteppedDialogForm, useSteppedDialogFormControls } from '@mews-commander/form';
import { createZodFormResolver, lt, showSuccessAlert } from '@mews/framework';
import { SelectedRateGroupsContextProvider, useSelectedRateGroupsContext } from '../hooks';
import { FormValues, validationSchema } from './schema';
import { useLinkRateGroups } from './queries';
import { SummaryStepContent } from './SumaryStepContent';
import { LinkStepFields } from './LinkStepFields';

type LinkRateGroupsFormStep = 'link' | 'summary';

export const useLinkRateGroupsDialogForm = ({
    selectedRateGroups,
    onSuccess,
}: {
    selectedRateGroups: PortfolioRateGroupOverview[];
    onSuccess?: () => void;
}) =>
    useSteppedDialogForm<LinkRateGroupsFormStep>({
        id: 'linkRateGroupForm',
        title: lt('LinkCentralSidePanelHeading'),
        steps: ['link', 'summary'],
        stepLabels: {
            link: lt('LinkCentralLinkStepLabel'),
            summary: lt('Summary'),
        },
        onSuccess,
        renderForm: (formProps) => (
            <SelectedRateGroupsContextProvider value={selectedRateGroups}>
                <LinkRateGroupsDialogFormContent {...formProps} />
            </SelectedRateGroupsContextProvider>
        ),
    });

const LinkRateGroupsDialogFormContent = (props: SteppedDialogFormProps<LinkRateGroupsFormStep>) => {
    const selectedRateGroups = useSelectedRateGroupsContext();
    const { mutateAsync } = useLinkRateGroups();
    const { renderFormContent } = useSteppedDialogFormControls<LinkRateGroupsFormStep, FormValues>({
        ...props,
        stepResolvers: {
            link: createZodFormResolver(validationSchema),
            summary: createZodFormResolver(validationSchema),
        },
        onSubmit: async (formValues) => {
            await mutateAsync(
                {
                    rateGroupIds: selectedRateGroups.map((rateGroup) => rateGroup.id),
                    centralRateGroupId: formValues.centralRateGroupId,
                },
                {
                    onSuccess: () => {
                        showSuccessAlert({
                            title: lt('LinkCentralNotificationSuccessHeading', { count: selectedRateGroups.length }),
                            description: lt('LinkCentralNotificationSuccessBody', { count: selectedRateGroups.length }),
                        });
                    },
                }
            );
        },
    });

    return renderFormContent(matchFormFields);
};

const matchFormFields = (step: LinkRateGroupsFormStep) => {
    switch (step) {
        case 'link':
            return <LinkStepFields />;
        case 'summary':
            return <SummaryStepContent />;
    }
};
